import React, { useEffect, useState } from 'react';
import { Modal } from '../shared/Modal';
import { FieldMapping, ObjectType, DataType, Transform, SectionConfig } from '../../types/metadata';
import { Info } from 'lucide-react';
interface FieldMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: FieldMapping | Omit<FieldMapping, 'id'>) => void;
  initialData?: FieldMapping;
  sections: SectionConfig[];
  preselectedSection?: string | null;
}
export function FieldMappingModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  sections,
  preselectedSection
}: FieldMappingModalProps) {
  const [formData, setFormData] = useState<Partial<FieldMapping>>({
    questionKey: '',
    section: preselectedSection || '',
    promptText: '',
    dataType: DataType.Text,
    targetSObject: ObjectType.Lead,
    targetFieldApiName: '',
    positionInSection: 1,
    required: false,
    isRequiredForCompletion: false,
    preferably: false,
    active: true,
    conditionKey: '',
    conditionValue: '',
    transform: Transform.None
  });
  useEffect(() => {
    if (initialData && isOpen) {
      setFormData(initialData);
    } else if (isOpen) {
      setFormData({
        questionKey: '',
        section: preselectedSection || (sections.length > 0 ? sections[0].sectionKey : ''),
        promptText: '',
        dataType: DataType.Text,
        targetSObject: ObjectType.Lead,
        targetFieldApiName: '',
        positionInSection: 1,
        required: false,
        isRequiredForCompletion: false,
        preferably: false,
        active: true,
        conditionKey: '',
        conditionValue: '',
        transform: Transform.None
      });
    }
  }, [initialData, isOpen, preselectedSection, sections]);
  // Update targetSObject when section changes
  useEffect(() => {
    if (formData.section) {
      const section = sections.find(s => s.sectionKey === formData.section);
      if (section && section.objectType !== formData.targetSObject) {
        setFormData(prev => ({
          ...prev,
          targetSObject: section.objectType
        }));
      }
    }
  }, [formData.section, sections]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.questionKey || !formData.section || !formData.targetSObject) return;
    onSave(formData as FieldMapping | Omit<FieldMapping, 'id'>);
    onClose();
  };
  const footer = <>
      <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-74">
        Cancel
      </button>
      <button type="submit" form="field-mapping-form" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700" data-id="element-75">
        {initialData ? 'Save Changes' : 'Create Field'}
      </button>
    </>;
  return <Modal isOpen={isOpen} onClose={onClose} title={initialData ? 'Edit Field Mapping' : 'New Field Mapping'} size="xl" footer={footer} data-id="element-76">
      <form id="field-mapping-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-77">
        {/* Group 1: Identity */}
        <fieldset className="p-4 border border-gray-200 rounded-xl bg-gray-50/50" data-id="element-78">
          <legend className="px-2 text-sm font-semibold text-gray-900" data-id="element-79">
            Identity & Display
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2" data-id="element-80">
            <div className="md:col-span-2" data-id="element-81">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-82">
                Prompt Text <span className="text-red-500" data-id="element-83">*</span>
              </label>
              <input type="text" required value={formData.promptText || ''} onChange={e => setFormData({
              ...formData,
              promptText: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400" placeholder="e.g., First Name" data-id="element-84" />
            </div>
            <div data-id="element-85">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-86">
                Question Key <span className="text-red-500" data-id="element-87">*</span>
              </label>
              <input type="text" required value={formData.questionKey || ''} onChange={e => setFormData({
              ...formData,
              questionKey: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-gray-600 placeholder:text-gray-400 placeholder:font-sans" placeholder="e.g., borrower.firstName" data-id="element-88" />
            </div>
            <div data-id="element-89">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-90">
                Section <span className="text-red-500" data-id="element-91">*</span>
              </label>
              <select required value={formData.section || ''} onChange={e => setFormData({
              ...formData,
              section: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" data-id="element-92">
                <option value="" disabled data-id="element-93">
                  Select a section...
                </option>
                {sections.map(s => <option key={s.id} value={s.sectionKey} data-id="element-94">
                    {s.sectionLabel} ({s.objectType})
                  </option>)}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Group 2: Data Configuration */}
        <fieldset className="p-4 border border-gray-200 rounded-xl bg-gray-50/50" data-id="element-95">
          <legend className="px-2 text-sm font-semibold text-gray-900" data-id="element-96">
            Data Configuration
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2" data-id="element-97">
            <div data-id="element-98">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-99">
                Data Type <span className="text-red-500" data-id="element-100">*</span>
              </label>
              <select required value={formData.dataType || DataType.Text} onChange={e => setFormData({
              ...formData,
              dataType: e.target.value as DataType
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" data-id="element-101">
                {Object.values(DataType).map(type => <option key={type} value={type} data-id="element-102">
                    {type}
                  </option>)}
              </select>
            </div>
            <div data-id="element-103">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-104">
                Target Object <span className="text-red-500" data-id="element-105">*</span>
              </label>
              <select required value={formData.targetSObject || ObjectType.Lead} onChange={e => setFormData({
              ...formData,
              targetSObject: e.target.value as ObjectType
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed" disabled title="Determined by selected section" data-id="element-106">
                {Object.values(ObjectType).map(type => <option key={type} value={type} data-id="element-107">
                    {type}
                  </option>)}
              </select>
            </div>
            <div data-id="element-108">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-109">
                Target Field API Name
              </label>
              <input type="text" value={formData.targetFieldApiName || ''} onChange={e => setFormData({
              ...formData,
              targetFieldApiName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-gray-600 placeholder:text-gray-400 placeholder:font-sans" placeholder="e.g., FirstName" data-id="element-110" />
            </div>
          </div>
        </fieldset>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-111">
          {/* Group 4: Requirements */}
          <fieldset className="p-4 border border-gray-200 rounded-xl bg-gray-50/50 self-start" data-id="element-112">
            <legend className="px-2 text-sm font-semibold text-gray-900" data-id="element-113">
              Requirements
            </legend>
            <div className="space-y-3 mt-2" data-id="element-114">
              <label className="flex items-center cursor-pointer" data-id="element-115">
                <input type="checkbox" checked={formData.required || false} onChange={e => setFormData({
                ...formData,
                required: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-116" />
                <span className="ml-2.5 text-sm text-gray-700" data-id="element-117">
                  Required (UI Validation)
                </span>
              </label>
              <label className="flex items-center cursor-pointer" data-id="element-118">
                <input type="checkbox" checked={formData.isRequiredForCompletion || false} onChange={e => setFormData({
                ...formData,
                isRequiredForCompletion: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-119" />
                <span className="ml-2.5 text-sm text-gray-700" data-id="element-120">
                  Required for 1003 Completion
                </span>
              </label>
              <label className="flex items-center cursor-pointer" data-id="element-121">
                <input type="checkbox" checked={formData.preferably || false} onChange={e => setFormData({
                ...formData,
                preferably: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-122" />
                <span className="ml-2.5 text-sm text-gray-700" data-id="element-123">
                  Preferred (Soft warning)
                </span>
              </label>
            </div>
          </fieldset>

          {/* Group 5: Conditional Visibility */}
          <fieldset className="p-4 border border-amber-200 rounded-xl bg-amber-50/30 self-start" data-id="element-124">
            <legend className="px-2 text-sm font-semibold text-amber-900" data-id="element-125">
              Conditional Visibility
            </legend>
            <div className="space-y-3 mt-2" data-id="element-126">
              <div data-id="element-127">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-128">
                  Condition Key
                </label>
                <input type="text" value={formData.conditionKey || ''} onChange={e => setFormData({
                ...formData,
                conditionKey: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-gray-600 placeholder:text-gray-400 placeholder:font-sans" placeholder="e.g., borrower.hasCoBorrower" data-id="element-129" />
              </div>
              <div data-id="element-130">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-131">
                  Condition Value
                </label>
                <input type="text" value={formData.conditionValue || ''} onChange={e => setFormData({
                ...formData,
                conditionValue: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400" placeholder="e.g., yes" data-id="element-132" />
              </div>
              <p className="text-xs text-amber-700 flex items-start gap-1.5" data-id="element-133">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" data-id="element-134" />
                Leave blank if field should always be visible.
              </p>
            </div>
          </fieldset>
        </div>

        {/* Group 6 & 7: Position, Transform & Status */}
        <fieldset className="p-4 border border-gray-200 rounded-xl bg-gray-50/50" data-id="element-135">
          <legend className="px-2 text-sm font-semibold text-gray-900" data-id="element-136">
            Position & Transform
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2" data-id="element-137">
            <div data-id="element-138">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-139">
                Position in Section
              </label>
              <input type="number" min="1" value={formData.positionInSection || 1} onChange={e => setFormData({
              ...formData,
              positionInSection: parseInt(e.target.value) || 1
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums" data-id="element-140" />
            </div>
            <div data-id="element-141">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-142">
                Data Transform
              </label>
              <select value={formData.transform || Transform.None} onChange={e => setFormData({
              ...formData,
              transform: e.target.value as Transform
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" data-id="element-143">
                {Object.values(Transform).map(t => <option key={t} value={t} data-id="element-144">
                    {t}
                  </option>)}
              </select>
            </div>
            <div className="flex items-center pt-6" data-id="element-145">
              <label className="flex items-center cursor-pointer" data-id="element-146">
                <div className="relative" data-id="element-147">
                  <input type="checkbox" className="sr-only" checked={formData.active ?? true} onChange={e => setFormData({
                  ...formData,
                  active: e.target.checked
                })} data-id="element-148" />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${formData.active ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-149"></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.active ? 'transform translate-x-4' : ''}`} data-id="element-150"></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700" data-id="element-151">
                  Active
                </span>
              </label>
            </div>
          </div>
        </fieldset>
      </form>
    </Modal>;
}